import { z } from 'zod';
import {
  getIsPrimary,
  getPrimaryCount,
  type OnSetPrimaryInvalidator,
} from '../NarrativeInvalidationBoundary';
import type { InfiniteData, QueryKey } from '@tanstack/react-query';
import type { Result } from './hooks/useNarrativesCommunityInfinite';
export const NARRATIVE_COMMUNITY_LIST_PAGE_SIZE = 9;
export const NARRATIVE_COMMUNITY_AFTER_QUERY_PARAM = 'after';
export const NARRATIVE_COMMUNITY_BEFORE_QUERY_PARAM = 'before';
/**
 * It will eagerly load more data by PRELOAD_OFFSET pixels before reaching the end of the page.
 * This will mean that people might not even notice that the page is loading more data unless scrolling aggressively.
 */
export const NARRATIVE_COMMUNITY_PRELOAD_OFFSET = 500;

const NarrativeListCursorSchema = z
  .string()
  .nullish()
  .transform((val) => val || undefined);

const NarrativeListPageParamsSchema = z.object({
  after: NarrativeListCursorSchema,
  before: NarrativeListCursorSchema,
});

export const narrativeListPageParamsSafeParse = (params: unknown) =>
  NarrativeListPageParamsSchema.safeParse(params).data ?? {
    after: undefined,
    before: undefined,
  };

export const narrativeListRemoveNilFields = <T extends Record<string, unknown>>(
  record: T | null | undefined
) => {
  const filteredRecord = Object.entries(record ?? {}).reduce(
    (acc, [key, value]) => {
      if (value === null || value === undefined) return acc;

      return { ...acc, [key]: value };
    },
    {} as T
  );
  return Object.keys(filteredRecord).length === 0 ? undefined : filteredRecord;
};

export const handleOnSetPrimary =
  (queryKey: QueryKey): OnSetPrimaryInvalidator =>
  ({ queryClient, invalidateExcept }, actionPayload) => {
    invalidateExcept(['narrative-community-list']);
    queryClient.setQueryData<InfiniteData<Result>>(queryKey, (state) => {
      if (!state) return;
      return {
        ...state,
        pages: state?.pages.map((page) => ({
          ...page,
          narratives: {
            ...page.narratives,
            edges: page.narratives.edges.map((edge) => {
              const node = edge.node;
              return {
                ...edge,
                node: {
                  ...node,
                  primaryCount: getPrimaryCount(node, actionPayload),
                  isPrimary: getIsPrimary(node, actionPayload),
                },
              };
            }),
          },
        })),
      };
    });
  };
